import { omit } from "@mixitone/util";
import { useLayoutEffect, useRef, useState } from "react";

interface Props extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  ref?: React.Ref<HTMLTextAreaElement>;
}
export const TextArea: React.FC<Props> = ({ ref: parentRef, ...props }) => {
  const [value, setValue] = useState(props.value);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const lastPropsValue = useRef(props.value);
  const rest = omit(props, ["value", "onChange"]);

  const ref = (element: HTMLTextAreaElement) => {
    textareaRef.current = element;
    if (typeof parentRef === "function") {
      parentRef(element);
    } else if (parentRef) {
      (parentRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = element;
    }
  };

  // Keystrokes update local state immediately. The parent catches up on a
  // microtask, so while this field is focused we ignore a props value that has
  // not changed. That stale echo is not a new edit.
  // A props value that did change (cleared on send, prefilled, restored after a
  // failed send) is applied even if the field is still the active element.
  // Disabling it on send does not move focus before this effect runs.
  useLayoutEffect(() => {
    const prevProps = lastPropsValue.current;
    lastPropsValue.current = props.value;

    if (props.value === value) return;

    const focused = textareaRef.current === document.activeElement;
    if (focused && props.value === prevProps) return;

    setValue(props.value);
  }, [props.value, value]);

  useLayoutEffect(() => {
    if (textareaRef.current) {
      // Reset height to allow shrinking
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    props.onChange(e);
  };

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={handleChange}
      rows={1}
      {...rest}
      className={`mt-1 block w-full rounded border border-gray-300 p-2 ${rest.className}`}
    />
  );
};
